import { OnDestroy, Injectable, inject } from "@angular/core";
import {
  Auth,
  User as FirebaseUser} from "@angular/fire/auth";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  deleteUser,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence
} from "@angular/fire/auth";
import {
  Firestore} from '@angular/fire/firestore';
import {
  doc,
  updateDoc,
  onSnapshot,
  setDoc,
  getDoc,
  collection,
  addDoc,
  increment,
  getDocs
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { environment } from 'environments/environment';
import { wrapCallableWithTiming } from '@core/services/data/callable-timing.util';
import { Router } from "@angular/router";
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { filter, take, switchMap, takeUntil } from 'rxjs/operators';
import { User } from '@shared/definitions/user';
import { Contractor } from 'src/app/shared/definitions/contractor';
import { WorkspaceStore } from '@shared/services/workspace.store';
import { LoggerService } from '@core/services/infra/logger.service';
import { SessionService } from '@core/services/auth/session.service';
import { AuthStore } from '@core/services/auth/auth.store';
// Type-only: erased at compile time, so this cannot form a runtime cycle with
// the coordinator (which injects AuthStore). The coordinator is deliberately
// NOT injected here — milestones travel out through a callback instead.
import type { SignupClientMilestone } from '@core/services/infra/authenticated-loading-coordinator.service';

/**
 * Phase 5 — shape of the post-signup initialization status document that
 * the backend Cloud Function writes at `users/{uid}/signup_status/initialization`.
 * Used by `waitForPostSignupInitialization` to track progress without `any`.
 */
export interface SignupInitializationStatus {
  completed?: boolean;
  customClaimsSet?: boolean;
  error?: string;
  step?: string;
  progress?: number;
  message?: string;
}

export interface ProfileData {
  firstName?: string;
  lastName?: string;
  gender?: string;
  phoneNumber?: string;
  [key: string]: string | undefined;
}


@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  private destroy$ = new Subject<void>();

  // Flag to prevent auth-shell from navigating during signup initialization
  private isInitializingSignup = false;
  isUserAuthenticated = new BehaviorSubject<boolean>(false);
  hasUserSeenDashboard = false;
  isNewUser = new BehaviorSubject<boolean>(false);
  user = new BehaviorSubject<User>({} as User);
  contractors = new BehaviorSubject<Contractor[]>([]);

  /**
   * Emits `true` once the Firebase ID token contains workspace custom claims.
   * Downstream data services (see `BaseCrudService.initializeService`) gate
   * their Firestore subscriptions on this so they don't fire workspace-scoped
   * reads before the claim has landed in the token — which produced
   * "Missing or insufficient permissions" spam on fresh signups.
   */
  claimsReady$ = new BehaviorSubject<boolean>(false);

  private userData: User = new User();

  /**
   * Backs the verification banner. Write ONLY through
   * {@link setEmailVerificationStatus} — a `true` here is sticky for the
   * session, which is what keeps a stale `emailVerified=false` claim from
   * flickering the banner back after a successful verification.
   */
  private emailVerificationStatus = new BehaviorSubject<boolean>(false);

  // Track migration to prevent infinite loops
  private migrationInProgress = new Set<string>();

  // Track users who have been processed to prevent infinite loops
  private processedUsers = new Set<string>();

  // Holds the active `users/{uid}` snapshot listener so we can dispose it
  // on logout / user switch. Without this, every login installs a new
  // listener while the previous one keeps running — and after sign-out
  // the stale listener fires "Missing or insufficient permissions".
  private userDocUnsubscribe: (() => void) | null = null;

  /**
   * The uid we've already run the heavy, side-effectful post-auth init for.
   * `signIn*` invoke `handleAuthStateChange` manually right after sign-in AND
   * Firebase's `onAuthStateChanged` fires it for the same identity — without
   * this guard the workspace re-initialized and the login event recorded twice.
   * Reset in `resetUserData` (sign-out and pre-login).
   */
  private initializedUid: string | null = null;

  /**
   * Timestamp of the last time `handleAuthStateChange` saw a real user —
   * fresh login or a session restored on page load. Used to tag how long
   * ago the user authenticated when reporting a slow `httpsCallable` (see
   * `reportSlowCallable`, issue #484): lets us compare callables fired right
   * after auth against ones fired mid-session instead of guessing.
   */
  private lastLoginAt: number | null = null;

  private workspaceStore = inject(WorkspaceStore);
  private logger = inject(LoggerService);
  private session = inject(SessionService);
  private authStore = inject(AuthStore);

  constructor(
    private auth: Auth,
    private router: Router,
    private firestore: Firestore,
    private fns: Functions
  ) {
    this.initializeAuth();
    this.bridgeBehaviorSubjectsToStore();
  }

  /**
   * Phase 4 — mirror every push into the signal-backed `AuthStore` so new
   * callers can consume via signals without touching BehaviorSubjects.
   * Kept as a single subscription block so removing the old subjects is
   * one localized edit once every consumer has been migrated.
   */
  private bridgeBehaviorSubjectsToStore(): void {
    this.user.pipe(takeUntil(this.destroy$)).subscribe(user => {
      // The subject is seeded with `{} as User` — only push non-empty users.
      this.authStore.setUser(user && user.userUID ? user : null);
    });
    this.isUserAuthenticated
      .pipe(takeUntil(this.destroy$))
      .subscribe(v => this.authStore.setIsAuthenticated(v));
    this.isNewUser
      .pipe(takeUntil(this.destroy$))
      .subscribe(v => this.authStore.setIsNewUser(v));
    this.emailVerificationStatus
      .pipe(takeUntil(this.destroy$))
      .subscribe(v => this.authStore.setEmailVerified(v));
    this.claimsReady$
      .pipe(takeUntil(this.destroy$))
      .subscribe(v => this.authStore.setClaimsReady(v));
  }

  /**
   * Inspect the current Firebase ID token for workspace custom claims and
   * emit the result on `claimsReady$`. Pulls from the cached token (no
   * network round-trip); call `refreshToken()` first if a forced refresh
   * is required (e.g. immediately after the signup CF sets the claims).
   */
  /**
   * Adopt the workspace straight from the ID token when it is unambiguous.
   *
   * The token's `workspaces` claim is `{ "<workspaceId>": "<role>" }`, and it is
   * already in hand here — `verifyAndEmitClaimsReady` parses it from the CACHED
   * token, no network. Without this, the id is only discovered by
   * `loadUserWorkspaces()`, a `where('members.<uid>', '!=', null)` query on the
   * `workspaces` collection followed by a `setCurrentWorkspace()` document read.
   * Every workspace-scoped listener sits behind that pair through
   * `waitForWorkspace()`, which polls in 100ms steps: measured at 1332ms of
   * dead waiting on a cold load (#234).
   *
   * Deliberately narrow, because picking the WRONG workspace shows the wrong
   * customer's data:
   *  - Only when the token carries EXACTLY ONE workspace. With several, the
   *    choice belongs to the user's persisted "last active" preference, which
   *    lives in Firestore — we do not guess, and the normal path still runs.
   *  - Only when nothing is selected yet, so a session-restored or
   *    user-switched workspace is never overridden.
   *
   * This is a head start, not a replacement: `loadUserWorkspaces()` still runs
   * and populates the full `Workspace` objects the UI needs. For a
   * single-workspace user its auto-selection lands on this same id, so no scope
   * change and no wasted re-fetch follow.
   */
  private seedWorkspaceFromClaims(workspaceIds: string[]): void {
    if (workspaceIds.length !== 1) return;
    this.workspaceStore.adoptWorkspaceIdFromClaims(workspaceIds[0]);
  }

  private async verifyAndEmitClaimsReady(): Promise<boolean> {
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      this.claimsReady$.next(false);
      return false;
    }
    try {
      const tokenResult = await currentUser.getIdTokenResult();
      const workspaceClaims = tokenResult?.claims?.['workspaces'] as
        | Record<string, string>
        | undefined;
      const workspaceIds = workspaceClaims ? Object.keys(workspaceClaims) : [];
      const hasClaims = workspaceIds.length > 0;
      this.seedWorkspaceFromClaims(workspaceIds);
      this.claimsReady$.next(hasClaims);
      return hasClaims;
    } catch (error) {
      console.error('Error verifying workspace claims on token:', error);
      this.claimsReady$.next(false);
      return false;
    }
  }

  /**
   * Force refresh the user's auth token to get updated custom claims
   * Useful after workspace membership changes
   */
  async refreshToken(): Promise<void> {
    const currentUser = this.auth.currentUser;
    if (currentUser) {
      try {
        await currentUser.getIdToken(true); // true = force refresh
        this.logger.debug('Auth token refreshed successfully', 'AuthService');
        // Re-inspect the refreshed token so `claimsReady$` reflects the
        // workspace claims we just pulled down.
        await this.verifyAndEmitClaimsReady();
      } catch (error) {
        console.error('Error refreshing auth token:', error);
        throw error;
      }
    }
  }

  /**
   * After a workspace-creation callable sets the user's `owner` custom claim,
   * that claim is NOT instantly visible on the client ID token: Firebase needs
   * a moment to propagate `setCustomUserClaims` to the token-minting service.
   * A forced `getIdToken(true)` immediately afterwards can still return a token
   * WITHOUT the new workspace claim, which then makes the claims-gated Firestore
   * read in `setCurrentWorkspace()` fail with permission-denied — silently
   * dropping the user back into their default personal workspace.
   *
   * This polls the refreshed token until the workspace claim appears (or we run
   * out of attempts) so the subsequent `switchWorkspace()` reliably succeeds.
   *
   * Public: `WorkspaceManagementService.create*Workspace()` (in-app workspace
   * creation, #465) calls this too — it used to call the plain `refreshToken()`
   * below instead, a single unpolled refresh that hits exactly the race
   * described above and silently bounces the owner back to their original
   * workspace after `switchWorkspace()`.
   */
  async waitForWorkspaceClaim(
    workspaceId: string,
    maxAttempts = 5,
  ): Promise<void> {
    const currentUser = this.auth.currentUser;
    if (!currentUser) return;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await currentUser.getIdToken(true); // force refresh
      const tokenResult = await currentUser.getIdTokenResult();
      const workspaces =
        (tokenResult.claims['workspaces'] as Record<string, string> | undefined) ?? {};
      if (workspaces[workspaceId]) {
        await this.verifyAndEmitClaimsReady();
        return;
      }
      // Claim not propagated yet — back off and retry. Exponential from a short
      // initial delay (0.15s, 0.3s, 0.6s, 1.2s, capped) so the typical case
      // (claim visible on the 2nd refresh) resolves fast while the worst case
      // stays bounded; same attempt count, so robustness is unchanged.
      await new Promise((resolve) =>
        setTimeout(resolve, Math.min(150 * 2 ** attempt, 1500)),
      );
    }

    console.warn(
      `[AuthService] Workspace claim for ${workspaceId} not visible on token after ${maxAttempts} attempts`,
    );
  }

  /**
   * Manually refresh custom claims from server and then refresh token
   * Use this when user gets "Missing or insufficient permissions" errors
   * This calls a Cloud Function to re-sync claims, then refreshes the local token
   */
  async refreshCustomClaims(): Promise<{ success: boolean; workspaceCount: number; workspaces: string[] }> {
    this.logger.debug('Refreshing custom claims from server', 'AuthService');

    try {
      // Call Cloud Function to re-sync claims
      const refreshClaimsFn = wrapCallableWithTiming(
        'refreshMyCustomClaims',
        httpsCallable(this.fns, 'refreshMyCustomClaims'),
        () => this.getMsSinceLogin(),
      );
      const result = await refreshClaimsFn({}) as { data: { success: boolean; workspaceCount: number; workspaces: string[] } };

      this.logger.debug('Claims synced on server', 'AuthService');

      // Now refresh the local token to get the updated claims
      await this.refreshToken();

      this.logger.debug('Local token refreshed with new claims', 'AuthService');

      return result.data;
    } catch (error: unknown) {
      console.error('Error refreshing custom claims:', error);
      throw error;
    }
  }

  /**
   * Milliseconds since `handleAuthStateChange` last saw a real user, or
   * `null` if that hasn't happened yet this page load. See `lastLoginAt`.
   */
  getMsSinceLogin(): number | null {
    return this.lastLoginAt === null ? null : Date.now() - this.lastLoginAt;
  }

  private initializeAuth(): void {
    // Scrub any legacy plaintext-credential blob that earlier versions may
    // have written. Safe to call unconditionally.
    this.session.scrubLegacyAuth();
    this.auth.onAuthStateChanged(this.handleAuthStateChange.bind(this));
  }

  private static readonly REMEMBER_ME_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

  private handleAuthStateChange(user: FirebaseUser | null): void {
    // First emission (user or null) means Firebase has finished restoring
    // persistence for this page load. Must run before any early return so
    // the boot curtain stops treating "not yet authenticated" as logged-out.
    this.authStore.setAuthResolved();

    if (user) {
      this.lastLoginAt = Date.now();
      // Soft 30-day expiry on "Remember me" sessions. If the user logged in
      // with rememberMe=true more than 30 days ago, force a re-login even if
      // Firebase's refresh token is still valid. Sessions without a stored
      // timestamp (rememberMe=false → SESSION persistence; or fresh signups
      // before the first login completes) skip this check.
      const loginAt = this.session.getRememberMeLoginAt();
      if (loginAt !== null && Date.now() - loginAt > AuthService.REMEMBER_ME_MAX_AGE_MS) {
        this.logger.info('[auth] Remember-me session expired (>30 days), forcing relogin', 'AuthService');
        this.session.clearRememberMeLoginAt();
        signOut(this.auth).catch(() => undefined);
        return;
      }

      this.userData.email = user.email || '';
      this.userData.userUID = user.uid;
      this.isUserAuthenticated.next(true);
      // A stale `emailVerified: false` on a token that predates the
      // verification must not re-show the banner; the single writer enforces
      // that (see setEmailVerificationStatus).
      this.setEmailVerificationStatus(user.emailVerified);

      // Run the heavy initialization once per identity. The cheap synchronous
      // state above still updates on every call (so a later token change such as
      // an emailVerified flip is reflected), but ensureUserDocument →
      // setupUserListener / recordLoginEvent / workspace reinitialize must not
      // run twice when signIn* invokes this manually AND onAuthStateChanged also
      // fires for the same uid.
      if (this.initializedUid === user.uid) {
        return;
      }
      this.initializedUid = user.uid;

      // Resolve `claimsReady$` from the cached token BEFORE emitting the
      // user. Returning users already have workspace claims cached, so
      // `claimsReady$` flips to true up front and data services can fire
      // immediately. Fresh signups stay `false` here — the signup init
      // flow's `refreshToken()` will flip it later once the CF sets the
      // claims. This is what prevents the "Missing or insufficient
      // permissions" burst on first load after signup.
      //
      // STARTED HERE, not inside the `.then()` below: it reads the CACHED ID
      // token (measured at 1ms) and depends on nothing but `auth.currentUser`,
      // which is already in hand. Sitting it behind `ensureUserDocument`
      // (measured 1067ms) and `recordLoginEvent` (289ms) delayed every
      // workspace-scoped Firestore listener by ~1.35s on every cold load —
      // the listeners are gated on `claimsReady`, so nothing could fetch until
      // two round-trips it does not depend on had finished (#234). The verdict
      // it emits is unchanged, only its timing.
      const claimsReady = this.verifyAndEmitClaimsReady();

      // Install the `users/{uid}` snapshot listener now rather than after
      // `ensureUserDocument`. Both read the same document; the listener is the
      // one that actually keeps `userData` up to date (it re-emits on every
      // snapshot and flips `userLoaded`), so waiting on a one-shot `getDoc`
      // first only added latency.
      this.setupUserListener(user.uid);

      claimsReady
        .then(() => {
          // Stream user data FIRST, THEN initialize workspace.
          // The workspace initialization needs access to this.userData via
          // authService.user.value.
          //
          // HOISTED out of the `ensureUserDocument` continuation (#234).
          // `streamUser()` is a one-liner — `this.user.next(this.userData)` —
          // and `userData.email` / `userData.userUID` are set synchronously
          // above, so it never depended on the user document: on this passive
          // path `ensureUserDocument` is a pure read that returns without
          // writing when the doc is missing, and it never touches `userData`.
          // Yet every workspace-scoped Firestore listener waits on this
          // emission (BaseCrudService's effect bails while `authStore.user()`
          // is null), so parking it behind a ~1.1s round-trip delayed all data
          // loading by that much on every cold load. Consumers receive the
          // same object, just sooner; `setupUserListener` re-emits the
          // enriched user when the document lands, and the onboarding guards
          // gate on `userLoaded` (set by that listener), not on this emission.
          this.streamUser();

          // Initialize workspace (user data is available)
          this.logger.debug('Starting workspace initialization', 'AuthService');
          return this.workspaceStore.reinitialize()
            .then(() => this.logger.debug('Workspace initialization complete', 'AuthService'))
            .catch(error => {
              console.error('Failed to initialize workspace:', error);
              // Don't throw - allow app to continue even if workspace fails
            });
        })
        .catch(error => console.error('Error in auth state change handler:', error));

      // OFF THE CRITICAL PATH (#234). Neither of these gates anything above.
      //
      // `ensureUserDocument` only matters for its create branch, which this
      // passive path cannot take (no `allowCreate`) — account creation belongs
      // to the gated signup flows. Kept for that guarantee, no longer awaited.
      //
      // `recordLoginEvent` is login bookkeeping: three sequential Firestore
      // round-trips (getDoc + updateDoc + addDoc), measured at 289ms. It
      // already swallows its own errors and its own comment says "login should
      // succeed even if logging fails", so it was never a precondition of
      // anything. Same treatment as `logContractActivity` on the
      // contract-creation path.
      void this.ensureUserDocument(user)
        .then(() => this.recordLoginEvent(user))
        .catch(error => console.error('Error in background auth bookkeeping:', error));
    } else {
      // Tear down the previous user's `users/{uid}` listener BEFORE we
      // reset state. Otherwise it stays open against the now-anonymous
      // client and fires a permission error on the next snapshot tick,
      // which is what was hanging the loading screen on user switch.
      if (this.userDocUnsubscribe) {
        this.userDocUnsubscribe();
        this.userDocUnsubscribe = null;
      }
      // Clear workspace on logout
      this.workspaceStore.clearWorkspace();
      this.isUserAuthenticated.next(false);
      // Identity change: the sticky `true` must not survive into the next user.
      this.setEmailVerificationStatus(false, { force: true });
      this.claimsReady$.next(false);
      this.resetUserData();
    }
  }

  /**
   * The ONLY writer of the email-verification banner state.
   *
   * `emailVerified` is monotonic per identity in this product: there is no
   * in-app email-change flow and no admin "unverify" action, so once an address
   * is proven it stays proven for the session. Making that explicit here is
   * what stops the banner oscillating — several independent sources (a forced
   * token refresh, an `onAuthStateChanged` carrying a not-yet-propagated claim,
   * the background reconcile) used to be able to re-assert `false` after a
   * successful verification, which is why "I've verified" appeared to need
   * several clicks.
   *
   * Identity transitions (sign-out, `resetUserData`) pass `force` to clear it.
   *
   * If an email-change or admin-unverify flow is ever added, it MUST call this
   * with `{ force: true }` — otherwise the stale `true` will survive.
   */
  private setEmailVerificationStatus(
    verified: boolean,
    opts?: { force?: boolean },
  ): void {
    if (verified) {
      if (!this.emailVerificationStatus.value) {
        this.emailVerificationStatus.next(true);
      }
      return;
    }
    if (!opts?.force && this.emailVerificationStatus.value) {
      return;
    }
    this.emailVerificationStatus.next(false);
  }

  private resetUserData(): void {
    this.userData = new User();
    this.user.next({} as User);
    this.isUserAuthenticated.next(false);
    this.isNewUser.next(false);
    this.claimsReady$.next(false);
    // We are between identities: the next user's `users/{uid}` document has
    // not been read yet. Drop the hydration flag so the onboarding guards
    // wait for the fresh snapshot before deciding (the setupUserListener
    // callback flips it back to true).
    this.authStore.setUserLoaded(false);
    // Clear migration tracking
    this.migrationInProgress.clear();
    this.processedUsers.clear();
    // Drop the sticky verified state so it cannot leak into the next identity.
    this.setEmailVerificationStatus(false, { force: true });
    // Force the next sign-in to re-run the heavy init (see handleAuthStateChange).
    this.initializedUid = null;
  }

  private setupUserListener(uid: string): void {
    this.logger.debug('Setting up user listener', 'AuthService');
    // Dispose any previous listener (logout-then-login, or switching uid)
    // before installing a new one, otherwise listeners accumulate and the
    // previous-user listener fires permission errors after sign-out.
    if (this.userDocUnsubscribe) {
      this.userDocUnsubscribe();
      this.userDocUnsubscribe = null;
    }
    this.userDocUnsubscribe = onSnapshot(
      doc(this.firestore, `users/${uid}`),
      (docSnapshot) => {
        // The `users/{uid}` document has now been read from Firestore at
        // least once. Flip the hydration flag so the onboarding guards stop
        // gating on a stale partial user (see AuthStore.userLoaded). Set it
        // regardless of whether the doc exists — the snapshot has resolved
        // the authoritative state either way.
        this.authStore.setUserLoaded(true);

        const userData = docSnapshot.data();
        if (userData) {
          // `verifyCustomEmailToken` writes `emailVerified: true` here right
          // after flipping the Auth record. This live snapshot is therefore a
          // push-based confirmation that costs nothing: the banner clears in
          // every open tab without a click, and without waiting on the token
          // claim to propagate.
          if (userData['emailVerified'] === true) {
            this.setEmailVerificationStatus(true);
          }

          // Create a new User instance with the data
          this.userData = new User({
            ...this.userData,
            ...userData
          });

          // Emit the updated user to subscribers
          this.user.next(this.userData);

          // Check onboarding status directly from user document
          const onboardingStatus = userData['onboardingStatus'];

          // Migration: If onboardingStatus is undefined but user has completed onboarding via old method
          if (!onboardingStatus && userData['preferences']?.onboardingCompleted && !this.processedUsers.has(this.userData.userUID)) {
            this.logger.debug('Migrating legacy onboarding status', 'AuthService');
            // Mark user as processed to prevent infinite loops
            this.processedUsers.add(this.userData.userUID);

            // Set the status locally to prevent showing onboarding
            this.userData.onboardingStatus = 'completed';

            // Try to update Firestore silently (don't block on this)
            if (!this.migrationInProgress.has(this.userData.userUID)) {
              this.migrationInProgress.add(this.userData.userUID);
              this.updateOnboardingStatus('completed').catch(error => {
                console.warn('Onboarding migration failed but continuing with local status:', error);
              }).finally(() => {
                this.migrationInProgress.delete(this.userData.userUID);
              });
            }

            this.isNewUser.next(false);
            this.streamUser();
            return;
          }

          // Show onboarding only if status is explicitly 'pending' or undefined (new users)
          const needsOnboarding = !onboardingStatus || onboardingStatus === 'pending';
          this.isNewUser.next(needsOnboarding);

          this.streamUser();
        } else {
          this.isNewUser.next(true);
        }
      },
      (error) => {
        console.error('Error in user document listener:', error);
      }
    );
  }

  private async ensureUserDocument(
    firebaseUser: FirebaseUser,
    profileData?: ProfileData,
    options?: { allowCreate?: boolean },
  ): Promise<void> {
    const userDocRef = doc(this.firestore, `users/${firebaseUser.uid}`);

    try {
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        // SECURITY: the passive auth-state path (onAuthStateChanged →
        // handleAuthStateChange, called WITHOUT allowCreate) must NEVER create
        // a brand-new `users/{uid}` document. Account creation belongs solely
        // to the gated signup / social-sign-in flows. Without this guard, a
        // first Google popup that the signup access-code gate then REJECTED
        // still left a doc behind — written by this racing listener while the
        // gate was awaiting its network check — so a second attempt saw an
        // existing account (`isNewAccount === false`) and bypassed the gate.
        if (!options?.allowCreate) {
          return;
        }
        const newUser = this.createNewUser(firebaseUser, profileData);

        try {
          await setDoc(userDocRef, newUser);
        } catch (error) {
          console.error('Error creating user document:', error);
          // Don't throw during initial auth state change - allow the flow to continue
          if (profileData) {
            throw error; // Only throw if this is an explicit signup call
          }
        }
      } else if (profileData) {
        try {
          await updateDoc(userDocRef, {
            ...profileData,
            updatedAt: new Date()
          });
        } catch (error) {
          console.error('Error updating user document:', error);
          throw error;
        }
      }
    } catch (error) {
      console.error('Error in ensureUserDocument:', error);
      // Don't throw during auth state changes to prevent breaking the flow
      if (profileData) {
        throw error; // Only throw if this is an explicit operation
      }
    }
  }

  private createNewUser(firebaseUser: FirebaseUser, profileData?: ProfileData): Partial<User> {
    const newUser: Partial<User> = {
      userUID: firebaseUser.uid,
      email: firebaseUser.email || '',
      roles: ['user'],
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Add profile data if available
    if (profileData) {
      return { ...newUser, ...profileData };
    }

    return newUser;
  }

  onUserHasSeenDashboard(): void {
    this.hasUserSeenDashboard = true;
  }

  streamUser(): void {
    this.user.next(this.userData);
  }

  /**
   * Wait for post-signup initialization using Firestore realtime listener
   * NO POLLING - Event-driven approach!
   *
   * @param userId The user ID to wait for
   * @param maxWaitTime Maximum time to wait in milliseconds (default: 30 seconds)
   * @param onProgress Optional callback for progress updates
   * @returns Promise that resolves when initialization is complete
   */
  /**
   * Check if signup initialization is in progress
   * Used by auth-shell to prevent premature navigation
   */
  isInitializingSignupProcess(): boolean {
    return this.isInitializingSignup;
  }

  /**
   * Validate a signup access code against the backend `verifyAccessCode`
   * callable (which checks the `SIGNUP_ACCESS_CODE` secret in Secret
   * Manager). Used by the PROD-only signup gate.
   *
   * Returns `true`/`false` ONLY for a definitive backend answer. A transport
   * error (CORS/IAM 403, App Check rejection, network outage) is RETHROWN, not
   * swallowed as `false`: swallowing it read to the user as "invalid code" even
   * when their code was correct — which masked a prod misconfiguration where
   * the callable wasn't publicly invokable. Callers distinguish "service
   * failed" (catch) from "code rejected" (returns `false`). Signup still fails
   * closed because a throw also aborts account creation.
   */
  async verifyAccessCode(code: string | undefined | null): Promise<boolean> {
    const trimmed = (code ?? '').trim();
    if (!trimmed) {
      return false;
    }
    const fn = httpsCallable<{ code: string }, { valid: boolean }>(
      this.fns,
      'verifyAccessCode',
    );
    const result = await fn({ code: trimmed });
    return result.data?.valid === true;
  }

  /**
   * Enforce the PROD-only signup access-code gate when CREATING a new
   * account. No-op when the gate is disabled (non-prod) or when the account
   * already exists (returning Google/Facebook/email users are never gated).
   * Throws an Error with `code = 'auth/invalid-access-code'` when the code is
   * missing or invalid.
   */
  private async enforceAccessCodeForNewAccount(
    isNewAccount: boolean,
    accessCode: string | undefined | null,
  ): Promise<void> {
    if (!isNewAccount || !environment.features.requireSignupAccessCode) {
      return;
    }
    const valid = await this.verifyAccessCode(accessCode);
    if (!valid) {
      const err: Error & { code?: string } = Object.assign(
        new Error('Invalid signup access code'),
        { code: 'auth/invalid-access-code' },
      );
      throw err;
    }
  }

  async waitForPostSignupInitialization(
    userId: string,
    maxWaitTime: number = 30000,
    onProgress?: (status: SignupInitializationStatus) => void,
    onMilestone?: (step: SignupClientMilestone) => void
  ): Promise<void> {
    this.logger.debug('Waiting for backend initialization', 'AuthService');
    this.isInitializingSignup = true;
    const startTime = Date.now();
    onMilestone?.('awaiting_backend');

    return new Promise<void>((resolve, reject) => {
      let hasResolved = false;

      const timeout = setTimeout(async () => {
        if (hasResolved) return;
        console.warn('[Post-Signup] Timeout after', maxWaitTime, 'ms — attempting claims-refresh recovery');
        // Last-ditch recovery: the CF chain writes custom claims via a
        // document trigger, but if it wrote the claims without writing the
        // final signup_status flag (or we missed the realtime event), the
        // claims may already be live server-side. Force a resync + token
        // refresh, then resolve if workspaces made it into the token.
        onMilestone?.('session_ready');
        try {
          await this.refreshCustomClaims();
          const tokenResult = await this.auth.currentUser?.getIdTokenResult();
          const workspaceClaims = tokenResult?.claims?.['workspaces'] as
            | Record<string, string>
            | undefined;
          if (workspaceClaims && Object.keys(workspaceClaims).length > 0) {
            hasResolved = true;
            unsubscribe();
            await this.workspaceStore.loadUserWorkspaces(userId);
            this.logger.info(`Post-signup recovered via claims-refresh after timeout`, 'AuthService');
            this.isInitializingSignup = false;
            resolve();
            return;
          }
        } catch (recoveryError) {
          console.error('[Post-Signup] Recovery attempt failed:', recoveryError);
        }

        if (hasResolved) return;
        hasResolved = true;
        unsubscribe();
        console.error('[Post-Signup] Timeout after', maxWaitTime, 'ms with no recovery path');
        this.isInitializingSignup = false;
        reject(new Error('Initialization timeout - please try logging in again'));
      }, maxWaitTime);

      // Listen to the initialization status document in real-time
      const statusDocRef = doc(this.firestore, `users/${userId}/signup_status/initialization`);

      // Also do a manual check after 5 seconds in case the listener misses the initial write
      setTimeout(async () => {
        if (!hasResolved) {
          try {
            const manualSnapshot = await getDoc(statusDocRef);
            if (manualSnapshot.exists()) {
              const status = manualSnapshot.data() as SignupInitializationStatus;
              if (status.completed && status.customClaimsSet && !hasResolved) {
                this.logger.debug('Manual check detected completion', 'AuthService');
              }
            }
          } catch (error) {
            console.error('[Post-Signup] Manual check error:', error);
          }
        }
      }, 5000);

      const unsubscribe = onSnapshot(
        statusDocRef,
        async (snapshot) => {
          if (snapshot.exists()) {
            const status = snapshot.data() as SignupInitializationStatus;

            // Notify UI of progress
            if (onProgress) {
              onProgress(status);
            }

            // Check if there was an error
            if (status.error) {
              clearTimeout(timeout);
              unsubscribe();
              console.error('[Post-Signup] Backend error:', status.error);
              reject(new Error(status.error));
              return;
            }

            // Check if initialization is complete
            if (status.completed && status.customClaimsSet) {
              if (hasResolved) {
                return;
              }

              hasResolved = true;
              clearTimeout(timeout);
              unsubscribe();

              this.logger.debug('Backend confirmed initialization complete', 'AuthService');

              // Refresh the token to get the claims. The backend is done, but
              // this + loadUserWorkspaces below is another silent stretch —
              // report it rather than let the bar sit on the last backend value.
              onMilestone?.('session_ready');
              try {
                await this.refreshToken();

                // Verify claims are in the token
                const tokenResult = await this.auth.currentUser?.getIdTokenResult();
                const workspaceClaims = tokenResult?.claims?.['workspaces'];

                if (workspaceClaims && Object.keys(workspaceClaims).length > 0) {
                  await this.workspaceStore.loadUserWorkspaces(userId);
                  const totalTime = Date.now() - startTime;
                  this.logger.info(`Post-signup initialization complete in ${totalTime}ms`, 'AuthService');
                  this.isInitializingSignup = false;
                  resolve();
                } else {
                  console.error('[Post-Signup] Claims not in token after refresh');
                  this.isInitializingSignup = false;
                  reject(new Error('Failed to load custom claims into token'));
                }
              } catch (error) {
                console.error('[Post-Signup] Error during token refresh:', error);
                this.isInitializingSignup = false;
                reject(error);
              }
            }
          }
        },
        (error) => {
          clearTimeout(timeout);
          console.error('[Post-Signup] Listener error:', error);
          this.isInitializingSignup = false;
          reject(error);
        }
      );
    }).catch((error) => {
      this.isInitializingSignup = false;
      throw error;
    });
  }

  /**
   * Wait for workspace_memberships subcollection to be created
   * This indicates the workspace creation cloud function has completed
   *
   * @param userId The user ID to check
   * @param timeout Maximum time to wait in milliseconds
   * @returns Promise<boolean> True if workspace was created, false if timeout
   */
  private async waitForWorkspaceMemberships(userId: string, timeout: number): Promise<boolean> {
    const startTime = Date.now();
    const checkInterval = 500; // Check every 500ms

    while (Date.now() - startTime < timeout) {
      try {
        // Check if workspace_memberships subcollection exists and has documents
        const membershipsRef = collection(this.firestore, `users/${userId}/workspace_memberships`);
        const snapshot = await getDocs(membershipsRef);

        if (!snapshot.empty) {
          return true;
        }

        // Wait before checking again
        await new Promise(resolve => setTimeout(resolve, checkInterval));
      } catch (error) {
        console.error('Error checking workspace memberships:', error);
        // Continue checking
      }
    }

    return false; // Timeout reached
  }

  async signup(
    email: string,
    password: string,
    profileData?: ProfileData,
    options?: {
      skipEmailVerification?: boolean;
      skipPersonalWorkspace?: boolean;
      /**
       * Client-observable checkpoints, so the signup overlay can advance on
       * what we already know instead of sitting at its entry value until the
       * first backend status arrives (which, on this path, is 66 %).
       */
      onMilestone?: (step: SignupClientMilestone) => void;
    },
  ): Promise<void> {
    try {
      this.logger.debug('Creating new user account', 'AuthService');

      // Brand-new account on this browser: wipe sessionStorage wholesale.
      // Onboarding sentinels (`*-just-finished`) left behind by a previous
      // user used to be scrubbed one key at a time here — that list already
      // drifted once (a second sentinel key existed and was never added to
      // it) and would silently drift again the next time someone adds a
      // step flag. Nothing that must survive a signup lives in
      // sessionStorage — invitations, remember-me, the chosen account type
      // all live in `localStorage` via `SessionService` — so a full clear is
      // the safe default. Without it, a stale sentinel makes the guard skip
      // the user-welcome / user-profile steps entirely — the user lands
      // directly on the workspace flow without ever giving us their
      // first/last name.
      try {
        sessionStorage.clear();
      } catch {
        /* ignore — sessionStorage unavailable in some contexts */
      }

      // Reset the in-memory user blob before swapping accounts. If a previous
      // user was logged in on this tab (or a stale `this.userData` survived a
      // hot reload), `setupUserListener` later spreads `...this.userData` over
      // the new doc snapshot — carrying over fields the new doc doesn't yet
      // contain, notably `profile.completedAt`. That makes
      // `OnboardingCompletedGuard` think the brand-new user has already
      // finished the personal block and jumps them straight to the workspace
      // flow.
      this.resetUserData();

      // See `completeSocialSignIn` — AuthShell navigates to '/' the instant
      // Firebase auth state flips, which fires as soon as
      // `createUserWithEmailAndPassword` resolves below, well before the
      // caller gets to call `waitForPostSignupInitialization` (the only other
      // place this flag was set). Without it, that early '/' navigation races
      // the explicit `router.navigate(['/onboarding'])` the caller issues once
      // this promise resolves: whichever one first reaches `/onboarding` with
      // no child segment falls through to the route config's default
      // `redirectTo: 'welcome'` — since Angular does not re-run the parent's
      // `canActivate` for a same-parent, different-child navigation, that
      // second, unguarded jump silently drops the user past the two
      // user-profile steps onto the workspace phase (issue: signup lands on
      // step 3/5 instead of step 1/5).
      this.isInitializingSignup = true;

      const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
      const firebaseUser = userCredential.user;
      options?.onMilestone?.('account_created');

      // Create user document FIRST so the onUserCreatedWorkspace Firestore
      // trigger can run while we fire the verification email in the background.
      // Sending it before the setDoc could add several seconds of latency
      // (SendGrid roundtrip) before the CF chain that provisions the
      // workspace + custom claims even starts — which is what produced
      // the post-signup spinner that appeared to hang.
      try {
        const userDocRef = doc(this.firestore, `users/${firebaseUser.uid}`);
        const basicUserData = {
          userUID: firebaseUser.uid,
          email: firebaseUser.email || email,
          createdAt: new Date(),
          updatedAt: new Date(),
          roles: ['user'],
          isActive: true,
          onboardingStatus: 'pending' as const,
          // The welcome email (sent by the `onUserCreatedSendWelcome` trigger)
          // now carries the verification link, so a new self-signup gets a
          // single email instead of two. Invited accounts skip it — the signed
          // invite link already proves the address.
          pendingEmailVerification: !options?.skipEmailVerification,
          // Tenant-invitation signups skip the auto-created personal PM
          // workspace: the `onUserCreatedWorkspace` trigger reads this flag and
          // returns early, so onboarding doesn't wait on a workspace the tenant
          // never uses (and the custom-claims race is avoided). The dedicated
          // tenant workspace is provisioned by `createTenantWorkspace`.
          ...(options?.skipPersonalWorkspace ? { skipPersonalWorkspace: true } : {}),
        };

        if (profileData) {
          Object.assign(basicUserData, profileData);
        }

        await setDoc(userDocRef, basicUserData);
        // The CF trigger chain is now guaranteed to be in flight — this is the
        // checkpoint the backend's `user_created` step would have reported on
        // the callable path.
        options?.onMilestone?.('user_doc_written');
        this.isNewUser.next(true);
      } catch (firestoreError) {
        console.error('Error creating user document:', firestoreError);
        // Don't throw — the auth account exists; user can complete profile later.
      }

      // The verification link is no longer sent as a separate email here: the
      // `onUserCreatedSendWelcome` trigger now embeds it in the welcome email
      // (gated on the `pendingEmailVerification` flag set above) so a new
      // self-signup receives one email instead of two. Users who need a fresh
      // link can request one from the in-app verification banner
      // (`resendCustomEmailVerification`). Invited accounts are already proven.

    } catch (error: unknown) {
      console.error('Could not create the user:', error);
      // Signup failed: don't leave AuthShell deferring its redirect forever
      // for whatever identity (if any) ends up authenticated next.
      this.isInitializingSignup = false;

      // Create a more user-friendly error for email already in use
      const firebaseError = error as { code?: string };
      if (firebaseError?.code === 'auth/email-already-in-use') {
        const customError: Error & { code?: string; email?: string } = Object.assign(
          new Error('Email already registered'),
          { code: 'auth/email-already-in-use', email }
        );
        throw customError;
      }

      // For other errors, throw as-is
      throw error;
    }
  }

  /**
   * Create a service provider workspace for a newly signed up service provider.
   * This should be called after signup when the account type is 'service_provider'.
   * It will create a service provider workspace instead of/in addition to the personal workspace.
   */
  async createServiceProviderWorkspace(invitationToken?: string): Promise<{ workspaceId: string }> {
    this.logger.debug('Creating service provider workspace', 'AuthService');

    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to create a service provider workspace');
    }

    try {
      const createWorkspace = httpsCallable<{ invitationToken?: string }, { success: boolean; workspaceId: string; error?: string }>(
        this.fns,
        'createServiceProviderWorkspace'
      );

      // When provisioning from an invitation link, pass the token so the backend
      // links the new workspace to the owner's provider entry in one batch.
      const result = await createWorkspace(invitationToken ? { invitationToken } : {});

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to create service provider workspace');
      }

      const workspaceId = result.data.workspaceId;

      // Wait for the freshly-written `owner` claim to land on the ID token
      // before switching, so the claims-gated Firestore read in
      // switchWorkspace() doesn't fail with permission-denied (see
      // waitForWorkspaceClaim). loadUserWorkspaces is best-effort.
      await this.waitForWorkspaceClaim(workspaceId);
      await this.workspaceStore.switchWorkspace(workspaceId);
      try {
        await this.workspaceStore.loadUserWorkspaces(currentUser.uid);
      } catch (listError) {
        console.warn('[AuthService] Provider workspace created but list refresh failed:', listError);
      }

      return { workspaceId };
    } catch (error: unknown) {
      console.error('[AuthService] Error creating service provider workspace:', error);
      throw error;
    }
  }

  /**
   * Create a real estate agency workspace for a newly signed up agency.
   * Called after signup when the account type is 'agency'. Adds an agency
   * workspace alongside the personal workspace and switches to it.
   */
  async createAgencyWorkspace(): Promise<{ workspaceId: string }> {
    this.logger.debug('Creating agency workspace', 'AuthService');

    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to create an agency workspace');
    }

    const createWorkspace = httpsCallable<Record<string, never>, { success: boolean; workspaceId: string; error?: string }>(
      this.fns,
      'createAgencyWorkspace'
    );

    const result = await createWorkspace({});
    if (!result.data.success) {
      throw new Error(result.data.error || 'Failed to create agency workspace');
    }

    const workspaceId = result.data.workspaceId;

    // Wait for the freshly-written `owner` claim to land on the ID token before
    // switching, so the claims-gated Firestore read inside switchWorkspace()
    // doesn't fail with permission-denied (which would silently bounce the user
    // back to their default personal workspace).
    await this.waitForWorkspaceClaim(workspaceId);
    await this.workspaceStore.switchWorkspace(workspaceId);
    // Refreshing the workspace list is best-effort: the agency is already the
    // active workspace, so a hiccup here must not abort the signup flow.
    try {
      await this.workspaceStore.loadUserWorkspaces(currentUser.uid);
    } catch (listError) {
      console.warn('[AuthService] Agency created but workspace list refresh failed:', listError);
    }

    return { workspaceId };
  }

  /**
   * Create a syndic (co-ownership manager) workspace for a newly signed up
   * syndic firm. Called after signup when the account type is 'syndic'.
   */
  async createSyndicWorkspace(): Promise<{ workspaceId: string }> {
    this.logger.debug('Creating syndic workspace', 'AuthService');

    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to create a syndic workspace');
    }

    const createWorkspace = httpsCallable<Record<string, never>, { success: boolean; workspaceId: string; error?: string }>(
      this.fns,
      'createSyndicWorkspace'
    );

    const result = await createWorkspace({});
    if (!result.data.success) {
      throw new Error(result.data.error || 'Failed to create syndic workspace');
    }

    const workspaceId = result.data.workspaceId;

    // Wait for the freshly-written `owner` claim to land on the ID token before
    // switching (see waitForWorkspaceClaim); loadUserWorkspaces is best-effort.
    await this.waitForWorkspaceClaim(workspaceId);
    await this.workspaceStore.switchWorkspace(workspaceId);
    try {
      await this.workspaceStore.loadUserWorkspaces(currentUser.uid);
    } catch (listError) {
      console.warn('[AuthService] Syndic workspace created but list refresh failed:', listError);
    }

    return { workspaceId };
  }

  /**
   * Accept a provider invitation (claim a service provider profile).
   * This links the current user's service provider workspace to the owner's provider entry.
   */
  async acceptProviderInvitation(token: string): Promise<{ success: boolean; workspaceId?: string }> {
    this.logger.debug('Accepting provider invitation', 'AuthService');

    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to accept a provider invitation');
    }

    try {
      const acceptInvitation = httpsCallable<{ invitationToken: string }, { success: boolean; workspaceId?: string; error?: string }>(
        this.fns,
        'acceptProviderInvitation'
      );

      const result = await acceptInvitation({ invitationToken: token });

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to accept provider invitation');
      }

      // Refresh token to get updated custom claims
      await this.refreshToken();

      // Clear the stored token
      this.session.clearLegacyProviderInvitation();

      return result.data;
    } catch (error: unknown) {
      console.error('[AuthService] Error accepting provider invitation:', error);
      throw error;
    }
  }

  /**
   * Accept a provider invitation for the CURRENTLY authenticated user AND enter
   * the provider workspace. Single entry point used by the provider-invitation
   * landing page (whether the user just signed up, just logged in, or was
   * already signed in). Mirrors {@link acceptTenantInvitationAndEnter}.
   *
   * Flow:
   *  1. `acceptProviderInvitation` — links the invitation to an EXISTING provider
   *     workspace server-side, or signals `requiresWorkspaceCreation` when the
   *     user has none yet.
   *  2. If a workspace must be created → `createServiceProviderWorkspace(token)`
   *     provisions it AND claims the invitation in one batch, waits for the
   *     `owner` claim, then switches into it.
   *  3. Otherwise → wait for the claim and switch into the linked workspace.
   *
   * Without this switch the user stays in their default/personal workspace —
   * which is exactly the "I'm logged in but not in provider mode" symptom.
   */
  async acceptProviderInvitationAndEnter(token: string): Promise<{ workspaceId: string }> {
    this.logger.debug('Accepting provider invitation and entering workspace', 'AuthService');

    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to accept a provider invitation');
    }

    const acceptInvitation = httpsCallable<
      { invitationToken: string },
      {
        success: boolean;
        workspaceId?: string;
        isExistingWorkspace?: boolean;
        requiresWorkspaceCreation?: boolean;
        error?: string;
      }
    >(this.fns, 'acceptProviderInvitation');

    const result = await acceptInvitation({ invitationToken: token });
    const data = result.data;

    if (!data.success) {
      throw new Error(data.error || 'Failed to accept provider invitation');
    }

    // No provider workspace yet → create one and link it via the same token.
    // createServiceProviderWorkspace handles the claim-wait + switchWorkspace.
    if (data.requiresWorkspaceCreation || !data.workspaceId) {
      const created = await this.createServiceProviderWorkspace(token);
      this.session.clearLegacyProviderInvitation();
      return created;
    }

    // An existing provider workspace was linked server-side → switch into it.
    const workspaceId = data.workspaceId;
    await this.refreshToken();
    await this.waitForWorkspaceClaim(workspaceId);
    await this.workspaceStore.switchWorkspace(workspaceId);
    try {
      await this.workspaceStore.loadUserWorkspaces(currentUser.uid);
    } catch (listError) {
      console.warn('[AuthService] Provider invitation accepted but workspace list refresh failed:', listError);
    }
    this.session.clearLegacyProviderInvitation();
    return { workspaceId };
  }

  /**
   * Accept a tenant invitation for the CURRENTLY authenticated user and enter
   * the tenant portal. This is the single entry point the tenant-invitation
   * landing page calls once the user has an account (whether they just signed
   * up, just logged in, or were already signed in).
   *
   * Flow:
   *  1. `acceptTenantInvitation` — covers the co-tenant join + existing tenant
   *     workspace link paths server-side. Returns `requiresWorkspaceCreation`
   *     when the user has no tenant workspace yet (the common first-time case).
   *  2. `createTenantWorkspace` — provisions the tenant workspace AND claims the
   *     invitation (contract projection) atomically, then sets the `owner`
   *     custom claim.
   *  3. Wait for the workspace claim to land on the ID token, then switch the
   *     active workspace to it so `TenantWorkspaceGuard` admits `/tenant/*`.
   *
   * Mirrors the professional `create*Workspace` helpers (claim wait + switch).
   */
  async acceptTenantInvitationAndEnter(
    invitationToken: string,
    /**
     * Ce que le locataire vient de dire de son numero, quand il l'a dit (#438).
     *
     * Toujours passe par DIFFUSION conditionnelle chez l'appelant : le schema
     * zod du callable est `.strict()`, et une cle presente valant `undefined`
     * reste une cle propre — elle ferait echouer l'acceptation entiere.
     */
    whatsapp: {
      whatsappOptIn?: boolean;
      whatsappPhone?: string;
      whatsappConfirmToken?: string;
    } = {},
  ): Promise<{ workspaceId: string; whatsappApplied?: boolean }> {
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to accept a tenant invitation');
    }

    const acceptInvitation = httpsCallable<
      {
        invitationToken: string;
        whatsappOptIn?: boolean;
        whatsappPhone?: string;
        whatsappConfirmToken?: string;
      },
      {
        success: boolean;
        workspaceId?: string;
        requiresWorkspaceCreation?: boolean;
        whatsappApplied?: boolean;
      }
    >(this.fns, 'acceptTenantInvitation');

    const acceptResult = (await acceptInvitation({ invitationToken, ...whatsapp })).data;
    if (!acceptResult.success) {
      throw new Error('Failed to accept tenant invitation');
    }

    // The accept callable marks the email verified server-side (the signed
    // invitation link proves email ownership). We must refresh the CLIENT state
    // so the "verify your email" banner auto-dismisses: a bare `getIdToken(true)`
    // refreshes the token but leaves `currentUser.emailVerified` stale AND never
    // re-emits onto the banner's `emailVerificationStatus` subject, so the banner
    // would persist until a manual click. `refreshEmailVerificationStatus()` does
    // the full job (reload() + getIdToken(true) + subject.next()), same as the
    // banner's own "verify" button.
    try {
      await this.refreshEmailVerificationStatus();
    } catch (refreshError) {
      console.warn('[AuthService] email-verification refresh after invitation accept failed:', refreshError);
    }

    let workspaceId = acceptResult.workspaceId;

    // First-ever claim of an unclaimed tenant entity → the user needs a fresh
    // tenant workspace. `createTenantWorkspace` also re-processes the invitation
    // claim, so it is safe to call here.
    if (acceptResult.requiresWorkspaceCreation || !workspaceId) {
      const createWorkspace = httpsCallable<
        { invitationToken: string },
        { success: boolean; workspaceId?: string; message?: string }
      >(this.fns, 'createTenantWorkspace');

      const createResult = (await createWorkspace({ invitationToken })).data;
      if (!createResult.success || !createResult.workspaceId) {
        throw new Error(createResult.message || 'Failed to create tenant workspace');
      }
      workspaceId = createResult.workspaceId;
    }

    // Make the tenant workspace active. Without this the TenantWorkspaceGuard
    // sees the user's default workspace (type !== 'tenant') and bounces them
    // back to `/`. We wait for the freshly-written claim first so the
    // claims-gated Firestore read inside switchWorkspace() doesn't fail with
    // permission-denied (see waitForWorkspaceClaim).
    await this.waitForWorkspaceClaim(workspaceId);
    await this.workspaceStore.switchWorkspace(workspaceId);
    try {
      await this.workspaceStore.loadUserWorkspaces(currentUser.uid);
    } catch (listError) {
      console.warn('[AuthService] Tenant invitation accepted but workspace list refresh failed:', listError);
    }

    return {
      workspaceId,
      // Remonte tel quel : la page d'invitation en fait une note, pas une
      // erreur. Un canal secondaire qui n'a pas pris ne doit pas transformer
      // une acceptation reussie en echec.
      ...(acceptResult.whatsappApplied === undefined
        ? {}
        : { whatsappApplied: acceptResult.whatsappApplied }),
    };
  }

  async login(email: string, password: string, rememberMe = false): Promise<void> {
    try {
      this.setRememberMe(rememberMe);

      // Persistence policy:
      //   rememberMe = true  → LOCAL persistence + 30-day soft expiry.
      //                        Survives tab close + browser restart, but
      //                        `handleAuthStateChange` forces a re-login
      //                        once the timestamp is older than 30 days.
      //   rememberMe = false → SESSION persistence. Cleared on tab close.
      await setPersistence(
        this.auth,
        rememberMe ? browserLocalPersistence : browserSessionPersistence
      );

      // Stamp / clear the remember-me login time. The timestamp is read on
      // app boot (`handleAuthStateChange`) to enforce the 30-day cap.
      if (rememberMe) {
        this.session.setRememberMeLoginAt(Date.now());
      } else {
        this.session.clearRememberMeLoginAt();
      }

      // Scrub any prior session's onboarding sentinels before we hand
      // off to the new identity — the guards rely on these flags as a
      // grace period, but they must reset between users.
      try {
        sessionStorage.removeItem('user-onboarding-just-finished');
        sessionStorage.removeItem('onboarding-just-finished');
      } catch {
        /* ignore */
      }

      // Reset the in-memory user blob before swapping accounts (mirrors
      // `signup`). Unlike logout — which does a full page reload — a login
      // that switches accounts in the same tab leaves `this.userData`
      // holding the previous user. `setupUserListener` later spreads
      // `...this.userData` over the new doc snapshot, carrying over fields
      // the new doc lacks, notably `profile.completedAt`. That makes the
      // OnboardingCompletedGuard think the new user already finished the
      // personal block and skips them straight to the workspace flow.
      this.resetUserData();

      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);

      // Remember the email (non-sensitive) so the login form can prefill it.
      if (rememberMe) {
        this.storeRememberedEmail(email);
      } else {
        this.clearRememberedEmail();
      }

      // Record login event
      await this.recordLoginEvent(userCredential.user);

      // Manually trigger auth state change
      this.handleAuthStateChange(this.auth.currentUser);
    } catch (error) {
      console.error("Error during login:", error);
      throw error;
    }
  }

  /**
   * Google Sign-In. With `intent: 'signup'` (the sign-up page) a first-ever
   * Google sign-in creates the account; with `intent: 'login'` (the login
   * page) it NEVER creates one — the brand-new identity is cleaned up and
   * rejected with `auth/account-not-found` so the caller can route the user
   * to `/auth/register`. Returning users behave identically under both.
   *
   * @param accessCode Optional signup access code. Only consulted in
   *   production AND only for brand-new accounts — returning users are never
   *   challenged. See {@link enforceAccessCodeForNewAccount}.
   * @param intent `'signup'` (default) may create the account; `'login'`
   *   rejects brand-new identities instead of creating them.
   * @returns `{ isNewAccount, uid }` so the caller can route a brand-new user
   *   into onboarding (returning users are auto-navigated by the auth-shell).
   */
  async signInWithGoogle(
    accessCode?: string,
    intent: 'login' | 'signup' = 'signup',
  ): Promise<{ isNewAccount: boolean; uid: string }> {
    try {
      return await this.completeSocialSignIn(new GoogleAuthProvider(), 'Google', accessCode, intent);
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  }

  /**
   * Facebook Sign-In. Same login/sign-up duality as {@link signInWithGoogle}.
   * Requires the Facebook provider to be enabled in Firebase Auth (App ID +
   * secret) and the Facebook app to be Live; while the app is under review the
   * popup only succeeds for the developer/test users declared in Meta.
   */
  async signInWithFacebook(
    accessCode?: string,
    intent: 'login' | 'signup' = 'signup',
  ): Promise<{ isNewAccount: boolean; uid: string }> {
    try {
      const provider = new FacebookAuthProvider();
      // We only need identity + email to create the account; ask for nothing more.
      provider.addScope('email');
      return await this.completeSocialSignIn(provider, 'Facebook', accessCode, intent);
    } catch (error) {
      console.error('Facebook sign-in error:', error);
      throw error;
    }
  }

  /**
   * Shared OAuth-popup sign-in flow behind {@link signInWithGoogle} /
   * {@link signInWithFacebook}. Opens the provider popup, applies the PROD
   * signup gate to brand-new accounts, ensures the Firestore user document,
   * and drives the onboarding signal — identically for every provider.
   */
  private async completeSocialSignIn(
    provider: GoogleAuthProvider | FacebookAuthProvider,
    providerLabel: string,
    accessCode?: string,
    intent: 'login' | 'signup' = 'signup',
  ): Promise<{ isNewAccount: boolean; uid: string }> {
    // See `signup` — sessionStorage must be wiped on every identity
    // transition (nothing that needs to survive a signup lives there).
    try {
      sessionStorage.clear();
    } catch {
      /* ignore */
    }

    // Reset the in-memory user blob before swapping accounts (see `login`) so
    // a previous account's `profile.completedAt` can't leak onto the new
    // identity via the `setupUserListener` merge.
    this.resetUserData();

    const result = await signInWithPopup(this.auth, provider);
    const user = result.user;
    const email = user.email;

    if (!email) {
      throw new Error(`No email found from ${providerLabel} account`);
    }

    // Decide new-vs-returning BEFORE creating the user document, so the
    // signup gate only ever challenges brand-new accounts.
    const userDocRef = doc(this.firestore, `users/${user.uid}`);
    const beforeSnap = await getDoc(userDocRef);
    const isNewAccount = !beforeSnap.exists();

    // LOGIN intent never creates an account. The popup just minted a Firebase
    // Auth entry for this brand-new identity — delete it (a registered user is
    // untouched: their users/{uid} doc makes isNewAccount false) so no orphan
    // record survives to look like a real account on the next attempt, then
    // surface a typed error the login page turns into a redirect to
    // /auth/register, where social sign-UP lives.
    if (isNewAccount && intent === 'login') {
      try {
        await deleteUser(user);
      } catch {
        await signOut(this.auth).catch(() => undefined);
      }
      throw Object.assign(
        new Error(`No account is registered for this ${providerLabel} identity`),
        { code: 'auth/account-not-found' },
      );
    }

    // PROD signup gate: filter who may CREATE an account. On failure, sign
    // the user back out so no orphan Firebase Auth session lingers (the
    // user doc was not created yet).
    if (isNewAccount) {
      try {
        await this.enforceAccessCodeForNewAccount(isNewAccount, accessCode);
      } catch (gateError) {
        // Gate rejected this brand-new account. DELETE the just-created Firebase
        // Auth account so nothing survives to bypass the gate on a retry. No
        // `users/{uid}` doc needs cleanup: the passive auth-state listener no
        // longer creates one (see ensureUserDocument) and the gated create
        // below was never reached. deleteUser also clears the session; fall back
        // to a plain signOut if the delete fails for any reason.
        try {
          await deleteUser(user);
        } catch {
          await signOut(this.auth).catch(() => undefined);
        }
        throw gateError;
      }
      // Flag signup-in-progress UP-FRONT so the auth-shell defers its
      // automatic "/" navigation. The caller then waits for the backend
      // workspace provisioning (waitForPostSignupInitialization) and routes
      // to /onboarding. Without this the app navigated to "/" before the
      // personal workspace existed and the onboarding guard timed out and
      // failed open — which is exactly why social sign-up skipped onboarding.
      this.isInitializingSignup = true;
    }

    // Seed the profile name from the provider account, but treat the
    // provider `displayName` strictly as a FALLBACK — never let it overwrite
    // a name the user has already set. Previously this ran on every social
    // sign-in and `ensureUserDocument` blindly `updateDoc`-ed the displayName
    // parts over the existing doc, so a returning Google user who had edited
    // their name in their profile got it reset to the Google displayName on
    // each login. We only fill firstName/lastName when the account is brand
    // new OR the existing doc has no name yet.
    const existingData = beforeSnap.data();
    const hasExistingName = !!(
      existingData?.['firstName'] || existingData?.['lastName']
    );
    let profileData: ProfileData | undefined;
    if (user.displayName && (isNewAccount || !hasExistingName)) {
      const nameParts = user.displayName.split(' ');
      profileData = {
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
      };
    }

    if (isNewAccount) {
      // Defense-in-depth: create the account through the GATED server callable
      // so the signup access code is re-verified server-side before the doc
      // exists, and the callable — not a racing client listener — is the sole
      // creator (it is also the intended path for the workspace/welcome
      // triggers; see createUserDocument.ts). The passive auth-state path never
      // creates (see ensureUserDocument), and the client gate above already
      // validated the code; this is the server backstop.
      const createUserDoc = httpsCallable(this.fns, 'createUserDocument');
      try {
        await createUserDoc({ accessCode });
      } catch (serverError) {
        // Server refused creation (e.g. the gate). Remove the orphan Firebase
        // Auth account and surface it as the standard access-code error the UI
        // routes to /auth/register.
        try {
          await deleteUser(user);
        } catch {
          await signOut(this.auth).catch(() => undefined);
        }
        const code = (serverError as { code?: string } | null)?.code;
        if (code === 'functions/permission-denied') {
          throw Object.assign(new Error('Invalid signup access code'), {
            code: 'auth/invalid-access-code',
          });
        }
        throw serverError;
      }
    } else {
      // Returning user: reconcile profile fields only — this never creates.
      await this.ensureUserDocument(user, profileData);
    }

    // Parity with email signup: a brand-new account starts onboarding as
    // 'pending'. Provider e-mails are inherently verified, so no custom
    // verification e-mail is sent and the verification banner stays hidden.
    if (isNewAccount) {
      try {
        await updateDoc(userDocRef, {
          onboardingStatus: 'pending',
          updatedAt: new Date(),
        });
      } catch (statusError) {
        console.warn(`Could not set onboardingStatus for new ${providerLabel} account:`, statusError);
      }
    }

    // Onboarding need is driven by `profile.completedAt` (what the guard
    // reads), NOT by name presence — the old name-based heuristic wrongly
    // marked social users as "not new" and skipped onboarding.
    const afterSnap = await getDoc(userDocRef);
    const userData = afterSnap.data();
    const needsOnboarding = isNewAccount || !userData?.['profile']?.['completedAt'];
    this.isNewUser.next(needsOnboarding);

    // Record login event
    await this.recordLoginEvent(user);

    // Manually trigger auth state change
    this.handleAuthStateChange(user);

    return { isNewAccount, uid: user.uid };
  }

  async tryAutoLogin(): Promise<boolean> {
    // Auto-login is handled natively by Firebase's browserLocalPersistence.
    // If the session is still valid, authState() emits the user and the
    // AuthGuard never reaches this fallback. If the session is gone we can't
    // re-login silently (and shouldn't — that would require a stored password).
    return false;
  }

  async logout(): Promise<void> {
    try {
      this.clearRememberedEmail();
      // Tear down listeners BEFORE `signOut`. Once Firebase clears the
      // auth context, any still-open Firestore listener will fire a
      // "Missing or insufficient permissions" snapshot error against the
      // anonymous client. Clearing the user-doc listener here, plus
      // flipping auth state so `BaseCrudService` effects unsubscribe via
      // `clearEntities`, removes that error window before sign-out runs.
      if (this.userDocUnsubscribe) {
        this.userDocUnsubscribe();
        this.userDocUnsubscribe = null;
      }
      this.workspaceStore.clearWorkspace();
      this.claimsReady$.next(false);
      this.resetUserData();
      this.session.clearRememberMeLoginAt();

      // Clear sessionStorage so the next user signing in on the same
      // browser/tab is not treated as if they had already completed
      // onboarding. Without this, a returning teammate hitting signup
      // skipped the user-welcome / user-profile steps because a flag
      // from the previous account was still set (see `signup`).
      try {
        sessionStorage.clear();
      } catch {
        /* ignore — sessionStorage unavailable in some contexts */
      }

      // Wipe the per-user cache layer that EnhancedCachingService writes to
      // localStorage under `cache:${key}`. Otherwise the next user (or even
      // the same user re-logging in this tab) reads stale data — a leading
      // cause of the "I have to F5 to make it work" UX issue.
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key && key.startsWith('cache:')) {
            localStorage.removeItem(key);
          }
        }
      } catch {
        // localStorage may be unavailable (Safari private mode, SSR) — ignore.
      }

      await signOut(this.auth);

      // Hard reload back to the login route. SPA-internal `router.navigate`
      // leaves a tab full of half-disposed signals/effects/Firestore listeners
      // that race the next sign-in (we observed multi-second hangs and stale
      // workspace state on the first post-logout login). A full document
      // navigation guarantees a clean slate and matches what the user did
      // manually before with F5.
      window.location.assign('/auth/login');
    } catch (error) {
      console.error('Error during signout:', error);
      throw error;
    }
  }

  // Session-storage concerns now live in SessionService. These methods
  // stay as thin delegates to preserve the public API for existing
  // callers (ActionPlan Phase 3 façade migration).
  private setRememberMe(remember: boolean): void {
    this.session.setRememberMe(remember);
  }

  private getRememberMe(): boolean {
    return this.session.getRememberMe();
  }

  private storeRememberedEmail(email: string): void {
    this.session.storeRememberedEmail(email);
  }

  getRememberedEmail(): string | null {
    return this.session.getRememberedEmail();
  }

  private clearRememberedEmail(): void {
    this.session.clearRememberedEmail();
  }

  async updateUserData(firstName: string, lastName: string, mobileNumber: string, contractorUID?: string): Promise<void> {
    try {
      const userDocRef = doc(this.firestore, `users/${this.userData.userUID}`);
      const updateData: Partial<User> = {
        firstName: firstName || "",
        lastName: lastName || "",
        mobileNumber: mobileNumber || "",
        // Keep phoneNumber for backward compatibility
        phoneNumber: mobileNumber || "",
        updatedAt: new Date()
      };
      if (contractorUID !== undefined) {
        updateData.contractorUID = contractorUID;
      }
      await updateDoc(userDocRef, updateData);

      // After successful update, set isNewUser to false
      this.isNewUser.next(false);
    } catch (error) {
      console.error("Error updating user data:", error);
      throw error;
    }
  }

  async updateUserPreferences(preferences: NonNullable<User['preferences']>): Promise<void> {
    try {
      if (!this.userData?.userUID) {
        throw new Error('User not authenticated');
      }

      const userDocRef = doc(this.firestore, `users/${this.userData.userUID}`);
      const updateData: Partial<User> = {
        preferences: preferences,
        updatedAt: new Date()
      };

      // Use setDoc with merge to handle both new and existing documents
      await setDoc(userDocRef, updateData, { merge: true });

      // Update local user data
      this.userData.preferences = preferences;
      this.user.next(this.userData);

    } catch (error) {
      console.error('Error updating user preferences:', error);
      throw error;
    }
  }

  async updateOnboardingStatus(status: 'pending' | 'completed' | 'skipped'): Promise<void> {
    try {
      if (!this.userData?.userUID) {
        throw new Error('User not authenticated');
      }

      const userDocRef = doc(this.firestore, `users/${this.userData.userUID}`);
      const updateData: Partial<User> = {
        onboardingStatus: status,
        updatedAt: new Date()
      };

      await updateDoc(userDocRef, updateData);

      // Update local user data
      this.userData.onboardingStatus = status;
      this.user.next(this.userData);

    } catch (error) {
      console.error("Error updating onboarding status:", error);
      throw error;
    }
  }

  getUser(): User {
    return this.userData;
  }

  async resetPassword(email: string, firstName?: string): Promise<void> {
    try {
      const sendCustomPasswordReset = httpsCallable(this.fns, 'sendCustomPasswordReset');
      await sendCustomPasswordReset({
        email: email,
        firstName: firstName || '',
        origin: window.location.origin // Pass current origin for dynamic base URL
      });
    } catch (error) {
      console.error('Error sending password reset email:', error);
      throw error;
    }
  }

  /**
   * Verify password reset token and set new password
   */
  async verifyPasswordResetToken(token: string, newPassword: string): Promise<{ success: boolean; message?: string }> {
    try {
      const verifyToken = httpsCallable(this.fns, 'verifyPasswordResetToken');
      const result = await verifyToken({ token, newPassword });

      const data = result.data as { success?: boolean; message?: string };
      if (data.success) {
        return { success: true, message: data.message };
      }
      return { success: false, message: data.message || 'Password reset failed' };
    } catch (error: unknown) {
      // Handle specific error cases for better UX
      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage.includes('already been used') || errorMessage.includes('already used')) {
        throw new Error('Reset token has already been used', { cause: error });
      } else if (errorMessage.includes('expired') || errorMessage.includes('deadline-exceeded')) {
        throw new Error('Reset token has expired', { cause: error });
      } else if (errorMessage.includes('Invalid') || errorMessage.includes('not-found')) {
        throw new Error('Invalid reset token', { cause: error });
      } else if (errorMessage.includes('characters')) {
        throw new Error('Password must be at least 6 characters long', { cause: error });
      }

      throw error;
    }
  }

  isAuthenticated(): Observable<boolean> {
    return this.isUserAuthenticated.asObservable();
  }

  isFirstTimeUser(): Observable<boolean> {
    return this.isNewUser.asObservable();
  }

  // Wait for user data to be loaded before checking if user is new
  waitForUserDataAndCheckOnboarding(): Observable<boolean> {
    return this.user.asObservable().pipe(
      filter((user: User) => !!user && !!user.userUID), // Wait until we have user data with UID
      take(1),
      switchMap(() => this.isFirstTimeUser().pipe(take(1)))
    );
  }

  isEmailVerified(): boolean {
    return this.auth.currentUser?.emailVerified || false;
  }

  getCurrentFirebaseUser() {
    return this.auth.currentUser;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Removed automatic email verification polling - users can manually refresh using the banner button

  /**
   * Refresh the current user's email verification status
   */
  async refreshEmailVerificationStatus(): Promise<boolean> {
    try {
      if (this.auth.currentUser) {
        await this.auth.currentUser.reload();
        // Force a fresh ID token so the `email_verified` claim updates server-side
        // is reflected here AND persisted — without this, `reload()` fixes the
        // in-memory user but the cached token keeps the stale claim, so the next
        // `onAuthStateChanged` (e.g. after navigation/reload) re-derives
        // emailVerified=false and the verification banner reappears. Mirrors the
        // tenant-invitation accept flow.
        await this.auth.currentUser.getIdToken(true);

        // One retry: a click that lands while the verification is still
        // propagating should not answer "not yet" and send the user back to
        // their mailbox for a link they already used.
        if (!this.auth.currentUser.emailVerified) {
          await new Promise(resolve => setTimeout(resolve, 600));
          await this.auth.currentUser?.reload();
        }

        const isVerified = this.auth.currentUser?.emailVerified ?? false;
        this.setEmailVerificationStatus(isVerified);
        return isVerified;
      }
    } catch (error) {
      console.error('Error refreshing email verification status:', error);
    }
    return false;
  }

  /**
   * Get observable for email verification status
   */
  getEmailVerificationStatus(): Observable<boolean> {
    return this.emailVerificationStatus.asObservable();
  }

  /**
   * Verify email using custom token (when user clicks verification link)
   */
  async verifyEmailWithToken(token: string): Promise<boolean> {
    // The verify-email screen can be entered twice for one link (a re-mount, a
    // second tab, the user pressing Back). Without this, the second call hits
    // the backend's "already used" precondition and the user is told their
    // brand-new link is spent. Share the in-flight promise instead.
    const inFlight = this.pendingEmailTokenConfirmations.get(token);
    if (inFlight) return inFlight;

    const confirmation = this.confirmEmailTokenOnce(token).finally(() => {
      this.pendingEmailTokenConfirmations.delete(token);
    });
    this.pendingEmailTokenConfirmations.set(token, confirmation);
    return confirmation;
  }

  private readonly pendingEmailTokenConfirmations = new Map<
    string,
    Promise<boolean>
  >();

  private async confirmEmailTokenOnce(token: string): Promise<boolean> {
    try {
      const verifyToken = httpsCallable(this.fns, 'verifyCustomEmailToken');
      const result = await verifyToken({ token });

      const data = result.data as { success?: boolean };
      if (data.success) {
        // The cloud function already flipped the server-side claim before
        // returning success, so this is authoritative — not a guess. Hide the
        // banner immediately instead of making the user wait on the
        // propagation delay + token round-trip. The sticky-true writer keeps it
        // hidden even if `onAuthStateChanged` then fires with a stale token.
        this.setEmailVerificationStatus(true);

        // Refresh the user + force a fresh ID token in the background so the
        // verified claim is also persisted client-side.
        void this.reconcileEmailVerifiedAfterOptimisticDismiss();
        return true; // Cloud function succeeded
      }
      return false;
    } catch (error: unknown) {
      const outcome = this.classifyVerifyTokenError(error);

      if (outcome === 'used') {
        // Token was already used - this means verification succeeded previously,
        // so reconcile in the background to dismiss the banner (e.g. the link
        // was opened in another tab whose status is still stale).
        void this.reconcileEmailVerifiedAfterOptimisticDismiss();
        throw new Error('Verification token has already been used', { cause: error });
      } else if (outcome === 'expired') {
        throw new Error('Verification token has expired', { cause: error });
      } else if (outcome === 'invalid') {
        throw new Error('Invalid verification token', { cause: error });
      }

      throw error;
    }
  }

  /**
   * Which of the three verification outcomes an error from
   * `verifyCustomEmailToken` represents, or `null` when it is none of them.
   *
   * Classifies on the callable's ERROR CODE first, which is unambiguous. The
   * previous version matched on the message only, and tested
   * `includes('expired')` BEFORE `includes('Invalid')` — so the backend's
   * not-found message, "Invalid **or expired** verification token", always
   * landed on "expired". An unknown link therefore claimed to have expired, on
   * the very screen that then offers to resend it (issue #287).
   *
   * The message tests remain as a fallback for an error that reaches us without
   * a code, but `has expired` is now required rather than the bare word.
   */
  private classifyVerifyTokenError(
    error: unknown
  ): 'used' | 'expired' | 'invalid' | null {
    const code = (error as { code?: string })?.code ?? '';
    const message = error instanceof Error ? error.message : '';

    if (
      code.endsWith('failed-precondition') ||
      message.includes('already been used') ||
      message.includes('already used')
    ) {
      return 'used';
    }
    if (code.endsWith('deadline-exceeded') || message.includes('has expired')) {
      return 'expired';
    }
    if (
      code.endsWith('not-found') ||
      message.includes('Invalid') ||
      message.includes('not found')
    ) {
      return 'invalid';
    }
    return null;
  }

  /**
   * Background reconciliation after a confirmed email verification.
   *
   * `reload()` + `getIdToken(true)` refresh and persist the `email_verified`
   * claim client-side so future `onAuthStateChanged` events stay verified.
   *
   * This deliberately does NOT roll the banner back when the claim hasn't
   * caught up. The cloud function calls `updateUser({emailVerified:true})`
   * BEFORE returning success, so a success response is authoritative and the
   * only thing being reconciled here is the client's own token cache. The old
   * rollback re-showed the banner ~3s after it disappeared, which is what made
   * "I've verified" look like it needed several clicks.
   */
  private async reconcileEmailVerifiedAfterOptimisticDismiss(): Promise<void> {
    const delaysMs = [0, 800, 2000, 4000];
    try {
      for (const delay of delaysMs) {
        if (delay > 0) {
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        if (!this.auth.currentUser) {
          return; // Signed out mid-flight — nothing to reconcile.
        }
        await this.auth.currentUser.reload();
        await this.auth.currentUser.getIdToken(true);
        if (this.auth.currentUser.emailVerified) {
          this.setEmailVerificationStatus(true);
          return;
        }
      }
      // The claim is still lagging. The CF already reported success, so we keep
      // the banner dismissed and let the `users/{uid}` snapshot (or the next
      // token refresh) confirm it.
      console.warn(
        '[AuthService] email_verified claim has not propagated yet; keeping the banner dismissed',
      );
    } catch (error) {
      console.error('Error reconciling email verification status:', error);
      // Keep the dismiss in place (see method docs).
    }
  }

  /**
   * Resend custom email verification.
   *
   * `language` is the language the user is reading the app in. It used to be
   * hardcoded to `'fr'` here, so a Dutch or German user who clicked "Renvoyer"
   * received a French mail (issue #287). Passed as a parameter rather than
   * injected: `TranslationService` pulls in ngx-translate's `TranslateService`,
   * and injecting it here would force every spec that instantiates AuthService
   * to provide the whole i18n graph. Omitting it keeps the previous behaviour —
   * the backend narrows an absent or unsupported value to the default.
   */
  async resendCustomEmailVerification(
    firstName?: string,
    language?: string
  ): Promise<void> {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser || !currentUser.email) {
        throw new Error('No user logged in or email not found');
      }

      const sendCustomVerification = httpsCallable(this.fns, 'sendCustomEmailVerification');
      await sendCustomVerification({
        email: currentUser.email,
        firstName: firstName || '',
        language,
        origin: window.location.origin // Pass current origin for dynamic base URL
      });

    } catch (error) {
      console.error('Error resending custom email verification:', error);
      throw error;
    }
  }

  /**
   * Ask the backend for a fresh verification link using an EXPIRED one as proof
   * — the signed-out path.
   *
   * `resendCustomEmailVerification` above reads `auth.currentUser`, so it cannot
   * work on `/auth/verify-email`: that screen lives under the auth shell, which
   * bounces authenticated users to `/`, so it is only ever seen signed OUT. The
   * stale token from the URL is the only credential available there, and the
   * backend resolves the account from it (issue #287).
   *
   * Rejects with an `HttpsError` whose `code` distinguishes the cases the caller
   * must word differently: `not-found` (link purged — the user has to sign in),
   * `failed-precondition` (already used), `resource-exhausted` (rate limit).
   */
  async resendEmailVerificationFromToken(
    token: string,
    language?: string
  ): Promise<{ alreadyVerified: boolean }> {
    const resendFromToken = httpsCallable<
      { token: string; language?: string; origin: string },
      { success: boolean; alreadyVerified?: boolean }
    >(this.fns, 'resendEmailVerificationFromToken');

    const result = await resendFromToken({
      token,
      language,
      origin: window.location.origin
    });

    return { alreadyVerified: result.data?.alreadyVerified === true };
  }

  /**
   * Record login event with timestamp and device info
   */
  private async recordLoginEvent(user: FirebaseUser): Promise<void> {
    try {
      const uid = user.uid;
      const userRef = doc(this.firestore, `users/${uid}`);

      // Check if we already recorded a login recently (avoid duplicates)
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const lastLogin = userData?.['lastLogin'];
        if (lastLogin) {
          const lastLoginTime = lastLogin.toDate ? lastLogin.toDate() : new Date(lastLogin);
          const timeDiff = Date.now() - lastLoginTime.getTime();
          // Skip if last login was within 30 seconds (likely duplicate call)
          if (timeDiff < 30000) {
            return;
          }
        }
      }

      const deviceInfo = this.getDeviceInfo();

      // Update user document with last login info
      await updateDoc(userRef, {
        lastLogin: new Date(),
        updatedAt: new Date(),
        'metadata.lastActivity': new Date(),
        'metadata.loginCount': increment(1),
        'metadata.deviceInfo': deviceInfo
      });

      // Add login history entry — path must match firestore.rules `login_history` match block
      const historyRef = collection(this.firestore, `users/${uid}/login_history`);
      await addDoc(historyRef, {
        timestamp: new Date(),
        device: deviceInfo,
        provider: user.providerData?.[0]?.providerId || 'password',
        success: true,
        ip: null // Could be populated by backend function if needed
      });

    } catch (error) {
      console.warn('recordLoginEvent warning (non-blocking):', error);
      // Don't throw - login should succeed even if logging fails
    }
  }

  /**
   * Get device information for login tracking
   */
  private getDeviceInfo(): string {
    try {
      const userAgent = navigator?.userAgent || '';
      const language = navigator?.language || '';
      const _platform = navigator?.platform || '';

      // Extract browser info
      let browser = 'Unknown';
      if (userAgent.includes('Chrome')) browser = 'Chrome';
      else if (userAgent.includes('Firefox')) browser = 'Firefox';
      else if (userAgent.includes('Safari')) browser = 'Safari';
      else if (userAgent.includes('Edge')) browser = 'Edge';

      // Extract OS info
      let os = 'Unknown';
      if (userAgent.includes('Windows')) os = 'Windows';
      else if (userAgent.includes('Mac')) os = 'macOS';
      else if (userAgent.includes('Linux')) os = 'Linux';
      else if (userAgent.includes('Android')) os = 'Android';
      else if (userAgent.includes('iOS')) os = 'iOS';

      return `${browser} on ${os} (${language})`;
    } catch (_error) {
      return 'Unknown Device';
    }
  }

  /**
   * Store invitation token for later processing after signup/login.
   * Used when new users click invitation links before they have an account.
   */
  storePendingInvitation(invitationId: string): void {
    this.session.storePendingInvitation(invitationId);
  }

  /**
   * Return the pending invitation token once and clear it.
   * The read is a consuming operation — downstream flows treat the call
   * as "invitation claimed".
   */
  getPendingInvitation(): string | null {
    return this.session.consumePendingInvitation();
  }

  /** Non-destructive read — see `SessionService.peekPendingInvitation`. */
  peekPendingInvitation(): string | null {
    return this.session.peekPendingInvitation();
  }

  hasPendingInvitation(): boolean {
    return this.session.hasPendingInvitation();
  }
}
